import React from 'react';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, SvgIcon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { borderRadius, boxShadows, palette, spaces } from '../../../../../assets/styles/themes/tokens';
import getMuiIcon, { IconName } from '../../../../../utils/getMuiIcon';

export interface MenuItemProps {
  label: string;
  iconName: IconName;
  onClick: () => void;
  color: string;
}

interface CustomMuiCardActionsProps {
  menuItems: MenuItemProps[];
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
  isMenuOpen: boolean;
}

const CustomMuiCardActions: React.FC<CustomMuiCardActionsProps> = (props) => {
  const { t } = useTranslation();
  const { menuItems, anchorEl, setAnchorEl, isMenuOpen } = props;

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
 
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
 
  const createMenuHandler = (callback: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    callback();
    handleMenuClose();
  };

  return (
    <>
      <IconButton
        data-testid='more-menu-button'
        onClick={handleMenuClick}
        color='secondary'
        size='small' 
        sx={{
          width: 32,
          height: 32,
        }}
        aria-label={t('More options')}
        aria-controls={isMenuOpen ? 'more-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={isMenuOpen ? 'true' : undefined}
      >
        <SvgIcon
          sx={{ 
            width: 'inherit',
            height: 'inherit',
          }}
          component={getMuiIcon('more')}
        /> 
      </IconButton>
      <Menu
        id='more-menu'
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: '293px',
            borderRadius: borderRadius.md,
            border: `1px solid ${palette.elevation.outlined}`,
            boxShadow: boxShadows.elevation4,
          },
        }}
        MenuListProps={{
          sx: { padding: spaces.xs },
        }}
      >
        {menuItems.map((item) => {
          return (
            <MenuItem
              key={item.label}
              onClick={createMenuHandler(item.onClick)}
              sx={{
                borderRadius: borderRadius.sm,
                gap: 0,
                color: item.color,
              }}
            >
              <ListItemIcon>
                <Icon
                  component={getMuiIcon(item.iconName)}
                  sx={{ fill: item.color, width: 'auto', height: 'auto' }}
                />
              </ListItemIcon>
              {t(item.label)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default CustomMuiCardActions;
